// src/components/ui/PromptHost.js
// ============================================================================
// Host del modal de ENTRADA DE TEXTO global. Se monta UNA sola vez en App.js.
// ----------------------------------------------------------------------------
// Escucha el singleton `preguntar` (src/utils/preguntar.js) y renderiza un
// <Modal> con un campo de texto + botones Cancelar / Aceptar. Resuelve la
// promesa con el texto ingresado, o null si se cancela. Tokenizado.
// ============================================================================
import React, { useState, useEffect, useRef } from 'react';
import Modal from '../Modal';
import { _registrarHostPrompt } from '../../utils/preguntar';
import { PencilSquareIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon } from './Icons';

function PromptHost() {
  const [actual, setActual] = useState(null);
  const [valor, setValor] = useState('');
  const inputRef = useRef(null);

  useEffect(() => _registrarHostPrompt((sol) => {
    setActual(sol);
    setValor(sol.valorInicial || '');
  }), []);

  // Foco automático al abrir.
  useEffect(() => {
    if (actual && inputRef.current) {
      const t = setTimeout(() => {
        inputRef.current?.focus();
        // auto-resize inicial del textarea
        if (inputRef.current.tagName === 'TEXTAREA') {
          inputRef.current.style.height = 'auto';
          inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
        }
      }, 60);
      return () => clearTimeout(t);
    }
  }, [actual]);

  const responder = (val) => {
    if (actual) {
      actual.resolver(val);
      setActual(null);
      setValor('');
    }
  };

  if (!actual) return null;

  const requerido = actual.requerido !== false;   // default: requerido
  const multilinea = actual.multilinea !== false;  // default: textarea
  const vacio = !valor.trim();
  const bloquearConfirmar = requerido && vacio;

  // peligro: tiñe el botón de confirmar en rojo + ícono de advertencia en el título.
  // Sirve para acciones destructivas (anular, eliminar) que antes encadenaban un
  // segundo modal `confirmar({ peligro })` — ahora se piden motivo + confirmación
  // en UNA sola ventana, sin encadenar (evita la carrera del History API).
  const peligro = !!actual.peligro;
  const colorAcento = peligro ? 'var(--color-peligro)' : 'var(--color-primario)';
  const TituloIcon = peligro ? ExclamationTriangleIcon : PencilSquareIcon;

  const confirmar = () => { if (!bloquearConfirmar) responder(valor); };

  const estiloCampo = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-borde-card)',
    background: 'var(--color-card)',
    color: 'var(--color-texto)',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  return (
    <Modal
      isOpen
      onClose={() => responder(null)}
      size="small"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: colorAcento, display: 'inline-flex', flexShrink: 0 }}>
            <TituloIcon className="w-6 h-6" />
          </span>
          <span>{actual.titulo || 'Ingresá un dato'}</span>
        </span>
      }
    >
      {actual.mensaje && (
        <p style={{ margin: '0 0 12px 0', lineHeight: 1.5, color: 'var(--color-texto)', whiteSpace: 'pre-line' }}>
          {actual.mensaje}
        </p>
      )}

      {multilinea ? (
        <textarea
          ref={inputRef}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
          placeholder={actual.placeholder || ''}
          rows={3}
          style={{ ...estiloCampo, resize: 'none', overflow: 'hidden', minHeight: '70px' }}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmar(); } }}
          placeholder={actual.placeholder || ''}
          style={estiloCampo}
        />
      )}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '20px' }}>
        <button
          className="btn-press hover-bright"
          onClick={() => responder(null)}
          style={{
            flex: '1 1 auto', minWidth: '120px', padding: '10px 20px', fontSize: '15px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            background: 'var(--color-neutro-solido)', color: 'white', border: 'none',
            borderRadius: '6px', cursor: 'pointer',
          }}
        >
          <XMarkIcon className="w-5 h-5" /> {actual.textoCancelar || 'Cancelar'}
        </button>
        <button
          className="btn-press hover-bright"
          onClick={confirmar}
          disabled={bloquearConfirmar}
          style={{
            flex: '1 1 auto', minWidth: '120px', padding: '10px 20px', fontSize: '15px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            background: bloquearConfirmar ? 'var(--color-neutro-solido)' : colorAcento,
            color: 'white', border: 'none', borderRadius: '6px',
            cursor: bloquearConfirmar ? 'not-allowed' : 'pointer',
            opacity: bloquearConfirmar ? 0.6 : 1,
          }}
        >
          <CheckIcon className="w-5 h-5" /> {actual.textoConfirmar || 'Aceptar'}
        </button>
      </div>
    </Modal>
  );
}

export default PromptHost;
